import { AnimationIcon } from "@dmm-com/rocket-web-ui-library";
import { memo, useCallback, useRef, useState } from "react";
import * as Styled from "./style";
import { usePurchased } from "./use-purchased";
import { useRouter } from "next/router";
import { ItemEpisode } from "~/components/blocks/product/common/blocks/item-episode";
import { ItemEpisodeDetailSkeleton } from "~/components/blocks/product/common/blocks/item-episode/detail-skeleton";
import { openDialog, closeDialog } from "~/components/stores/dialog/store";
import { PATHS } from "~/const/paths";
import { useImageSrcFormat } from "~/hooks/use-image-src-format";
import type { G_VideoSeasonType } from "~/lib/graphql";
import { G_VideoPpvExpirationType, G_VideoContentType } from "~/lib/graphql";
import { calculatePlayProgressRate } from "~/util/calculate-play-progress-rate";
import { durationTimeText } from "~/util/duration-time-text";
import { formatDateToYMDHm } from "~/util/format-date";

/** undefinedの時は何も表示しない */
let purchaseTabPurchasedText: "その他の購入方法" | undefined;
/** undefinedの時は何も表示しない */
let purchaseTabExpirationText: string | undefined;

type PurchasedProps = {
  /** シーズンタイプ（購入モーダルのUI構築のために必要） */
  seasonType: G_VideoSeasonType;
  /** 購入済みの合計数 */
  totalPurchasedLength: number;
};

/**
 * 購入済み一覧を表示するコンポーネント（舞台と複数話で使用する）
 * @param props {@link PurchasedProps}
 */
export const Purchased = memo((props: PurchasedProps) => {
  const { seasonType, totalPurchasedLength } = props;

  const { push } = useRouter();
  const { imageSrcFormat } = useImageSrcFormat({ aspectRatio: "LANDSCAPE" });

  const ref = useRef<HTMLDivElement>(null);
  const [isLoadingFetchMore, setIsLoadingFetchMore] = useState(false);

  const onSetIsLoadingFetchMore = useCallback(
    (isLoading: boolean) => setIsLoadingFetchMore(isLoading),
    [isLoadingFetchMore]
  );

  const {
    itemHeight,
    imageWidth,
    imageHeight,
    isVideoPurchasedEpisodeLoading,
    purchasedEdges,
    purchasedEpisodeDownloadBtnClicked,
    episodePurchasedBtnClicked,
  } = usePurchased({ ref, totalPurchasedLength, onSetIsLoadingFetchMore });

  return (
    <Styled.DivPurchasedRoot ref={ref}>
      {!isVideoPurchasedEpisodeLoading && purchasedEdges !== undefined ? (
        <>
          {purchasedEdges.map(({ node }, idx) => {
            const {
              ppvProducts,
              viewingRights,
              ppvExpiration,
              contentType,
              playInfo: { contentId, parts, duration },
            } = node;

            const part = parts.find((part) => part.contentId === contentId);
            const progressBarRate =
              part !== undefined && part.resumePoint !== 0
                ? calculatePlayProgressRate(part.resumePoint, part.duration)
                : undefined;

            // その他の購入方法ボタンを表示するかどうかの出し分けのフロー
            if (viewingRights.isStreamable) {
              const isAllPurchased = ppvProducts.every((cur) => cur.isPurchased);
              const isNotAllPurchased = ppvProducts.every((cur) => !cur.isPurchased);

              if (ppvProducts.length === 0) purchaseTabPurchasedText = undefined;
              if (ppvProducts.length === 1 && isAllPurchased) purchaseTabPurchasedText = undefined;
              if (ppvProducts.length > 1 && isAllPurchased) purchaseTabPurchasedText = undefined;
              if (ppvProducts.length > 1 && !isAllPurchased && !isNotAllPurchased)
                purchaseTabPurchasedText = "その他の購入方法";
              if (ppvProducts.length > 0 && isNotAllPurchased) purchaseTabPurchasedText = "その他の購入方法";
            }

            // 視聴期限とその出し分けのフロー（舞台と複数話の場合）
            if (contentType === G_VideoContentType.LiveVr || contentType === G_VideoContentType.Live_2D) {
              purchaseTabExpirationText = "映像配信終了(終演)時点まで";
            } else {
              if (ppvExpiration !== undefined && ppvExpiration !== null) {
                const { expirationType, viewingExpiration, viewingStartExpiration, startDeliveryAt } = ppvExpiration;

                if (expirationType === G_VideoPpvExpirationType.Est) {
                  purchaseTabExpirationText = "視聴期限：無期限";
                }

                if (expirationType === G_VideoPpvExpirationType.TvodDynamicExpiration) {
                  if (!viewingExpiration) {
                    if (viewingStartExpiration) {
                      purchaseTabExpirationText = `${formatDateToYMDHm(
                        viewingStartExpiration
                      )}までに視聴開始してください`;
                    }
                  } else {
                    purchaseTabExpirationText = `視聴期限：${formatDateToYMDHm(viewingExpiration)}`;
                  }
                }

                if (expirationType === G_VideoPpvExpirationType.TvodFixedExpiration) {
                  if (viewingExpiration && startDeliveryAt) {
                    /** クライアントの時刻が 配信開始日より前 の場合 */
                    const isBeforeDeliveryDate = new Date(startDeliveryAt).getTime() > Date.now();

                    if (isBeforeDeliveryDate) {
                      purchaseTabExpirationText = `視聴期限：${formatDateToYMDHm(
                        startDeliveryAt
                      )} - ${formatDateToYMDHm(viewingExpiration)}`;
                    } else {
                      // クライアントの時刻が 配信開始日より後 の場合
                      purchaseTabExpirationText = `視聴期限：${formatDateToYMDHm(viewingExpiration)}`;
                    }
                  }
                }
              } else {
                // 条件に当てはまらなかった場合は「undefined」を代入する
                purchaseTabExpirationText = undefined;
              }
            }

            return (
              <ItemEpisode
                key={idx}
                {...imageSrcFormat({ imgSrc: node.episodeImage })}
                alt={node.episodeTitle}
                episodeImageWidth={imageWidth}
                episodeImageHeight={imageHeight}
                episodeTitle={node.episodeTitle}
                episodeDescription={node.episodeDetail}
                episodeNumberName={node.episodeNumberName}
                supplementText={purchaseTabExpirationText}
                purchasedText={purchaseTabPurchasedText}
                // MEMO : ライブコンテンツの場合、右下に分数のタグを表示しない。ライブコンテンツ以外は右下に分数のタグを表示する
                imgSupplementText={
                  contentType === G_VideoContentType.LiveVr || contentType === G_VideoContentType.Live_2D
                    ? undefined
                    : durationTimeText(duration)
                }
                aspectRatio="9/16"
                visiblePlayIcon // MEMO（kimura）: 再生アイコンは常に表示すること
                isDownloadIcon={viewingRights.isDownloadable}
                // resumePointの値が「0」または「undefined」の場合は「レジュームバー」を表示しないように制御
                playedProgressRate={progressBarRate}
                onIconClick={() => {
                  const downloadModalData = {
                    titleName: node.episodeTitle,
                    seasonType,
                    downloadableFiles: node.viewingRights.downloadableFiles,
                  };

                  purchasedEpisodeDownloadBtnClicked(node.id, downloadModalData);
                }}
                onPurchasedBtnClick={() => episodePurchasedBtnClicked(node.id)}
                onThumbnailClick={async () => {
                  // MEMO : 配信前ライブ作品が入ってくる可能性があるので、isStreamableがfalseの場合は配信開始前ダイアログを表示する
                  if (!viewingRights.isStreamable) {
                    openDialog({
                      title: "この作品は配信前です",
                      description:
                        "配信開始日時までしばらくお待ちください。\n配信開始日時になりましたらページを更新してください。",
                      mainButtonName: "閉じる",
                      mainButtonLabelText: "閉じる",
                      isCancelButton: false,
                      isDisabledAnimation: true,
                      onMainButtonAction: closeDialog,
                    });

                    return;
                  }

                  void push(
                    {
                      pathname: PATHS.VOD_PLAYER,
                      query: {
                        season: node.seasonId,
                        content: node.id,
                      },
                    },
                    undefined,
                    {
                      shallow: true,
                    }
                  );
                }}
              />
            );
          })}

          {isLoadingFetchMore && (
            <Styled.DivLoadingWrapper>
              <AnimationIcon name="loading" iconSize="XXXL" />
            </Styled.DivLoadingWrapper>
          )}
        </>
      ) : (
        <ItemEpisodeDetailSkeleton
          skeletonLength={20}
          oneItemWidthRatio={100}
          itemHeight={itemHeight}
          imageWidth={imageWidth}
          imageHeight={imageHeight}
        />
      )}
    </Styled.DivPurchasedRoot>
  );
});

Purchased.displayName = "Purchased";
